import { createFileRoute, Link } from '@tanstack/react-router';
import { formatDateTimeShort } from '@/lib/dates';
import { useState } from 'react';
import {
  Activity,
  RefreshCw,
  Search,
  Filter,
  ChevronRight,
  Zap,
} from 'lucide-react';
import {
  usePipelineExecutions,
  usePipelineDefinitions,
  usePollQueue,
  useStaleCheck,
  useExecutionRealtimeUpdates,
  StatusBadge,
  ActionButton,
  type PipelineStatus,
} from '@/components/hermes';
import { toast } from 'sonner';

export const Route = createFileRoute('/hermes/executions')({
  component: ExecutionsPage,
});

function ExecutionsPage() {
  const [statusFilter, setStatusFilter] = useState<PipelineStatus | ''>('');
  const [pipelineFilter, setPipelineFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const { data: executions, isLoading, refetch } = usePipelineExecutions({
    status: statusFilter || undefined,
    pipelineId: pipelineFilter || undefined,
  });

  const { data: definitions } = usePipelineDefinitions();
  const pollMutation = usePollQueue();
  const staleMutation = useStaleCheck();

  // Enable realtime updates
  useExecutionRealtimeUpdates();

  const handlePoll = async () => {
    const result = await pollMutation.mutateAsync();
    toast.success(`Processed ${result.processed} tasks`);
  };

  const handleStaleCheck = async () => {
    const result = await staleMutation.mutateAsync();
    toast.success(`Found ${result.found} stale tasks`);
  };

  const filteredExecutions = executions?.filter((execution) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      execution.correlation_id.toLowerCase().includes(search) ||
      execution.pipeline_definitions?.name?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <Link to="/hermes" className="hover:text-foreground">Hermes</Link>
            <ChevronRight className="w-4 h-4" />
            <span>Executions</span>
          </div>
          <h1 className="text-2xl font-bold text-foreground">Pipeline Executions</h1>
        </div>
        <div className="flex items-center gap-2">
          <ActionButton onClick={handlePoll} size="sm">
            <Zap className="w-3.5 h-3.5" />
            Poll
          </ActionButton>
          <ActionButton onClick={handleStaleCheck} size="sm">
            <RefreshCw className="w-3.5 h-3.5" />
            Stale Check
          </ActionButton>
          <ActionButton onClick={async () => { await refetch(); }} size="sm" variant="ghost">
            <RefreshCw className="w-3.5 h-3.5" />
          </ActionButton>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by correlation ID or pipeline..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as PipelineStatus | '')}
          className="px-3 py-2 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="running">Running</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="cancelled">Cancelled</option>
        </select>

        <select
          value={pipelineFilter}
          onChange={(e) => setPipelineFilter(e.target.value)}
          className="px-3 py-2 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="">All Pipelines</option>
          {definitions?.map((def) => (
            <option key={def.id} value={def.id}>{def.name}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-secondary/50 border-b border-border">
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Pipeline
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Correlation ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Started
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Duration
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                    Loading...
                  </td>
                </tr>
              ) : filteredExecutions?.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                    No executions found
                  </td>
                </tr>
              ) : (
                filteredExecutions?.map((execution) => (
                  <tr
                    key={execution.id}
                    className="hover:bg-secondary/30 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <Link
                        to="/hermes/executions/$executionId"
                        params={{ executionId: execution.id }}
                        className="font-medium text-foreground hover:text-primary"
                      >
                        {execution.pipeline_definitions?.name ?? 'Unknown'}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge
                        status={execution.status}
                        animated={execution.status === 'running' || execution.status === 'in_progress'}
                        size="sm"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-xs text-muted-foreground font-mono">
                        {(execution.correlation_id ?? execution.id)?.slice(0, 16) ?? 'Unknown'}...
                      </code>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {execution.started_at
                        ? formatDateTime(execution.started_at)
                        : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {calculateDuration(execution.started_at, execution.completed_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;

  return formatDateTimeShort(date);
}

function calculateDuration(startedAt: string | null, completedAt: string | null): string {
  if (!startedAt) return '-';

  const start = new Date(startedAt);
  const end = completedAt ? new Date(completedAt) : new Date();
  const diffMs = end.getTime() - start.getTime();

  if (diffMs < 1000) return `${diffMs}ms`;
  if (diffMs < 60000) return `${(diffMs / 1000).toFixed(1)}s`;
  if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ${Math.floor((diffMs % 60000) / 1000)}s`;

  const hours = Math.floor(diffMs / 3600000);
  const mins = Math.floor((diffMs % 3600000) / 60000);
  return `${hours}h ${mins}m`;
}
